export function fetchJson(url, opts) {
  return fetch(url, opts).then(async (res) => {
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
    return data;
  });
}

export function textFromField(field) {
  if (!field) return '';
  return String(field.text || field.full_text_repr || '').trim();
}

export function inferPageRange(record) {
  const qPages = Array.isArray(record?.question?.page_indices) ? record.question.page_indices : [];
  const aPages = Array.isArray(record?.answer?.page_indices) ? record.answer.page_indices : [];
  const pages = [...qPages, ...aPages].map((x) => Number(x)).filter((x) => Number.isFinite(x));
  if (!pages.length) return null;
  return [Math.min(...pages), Math.max(...pages)];
}

export function normalizeRecord(raw, idx) {
  const r = raw || {};
  // The API returns flat records with:
  //   { "id": "...", "text": "...(question)...", "type": "oe|mcq", "answer": "...(answer string)..." }
  // Normalize into nested { question: { text: ... }, answer: { text: ... } } shape for the UI.
  const questionText = String(r.text || r.question?.text || '').trim();
  const answerText = r.answer != null ? String(typeof r.answer === 'string' ? r.answer : r.answer.text || '') : null;
  const q = { text: questionText, full_text_repr: questionText };
  const a = answerText != null ? { text: answerText, full_text_repr: answerText } : null;

  const id = String(r.id || `qa_${String(idx + 1).padStart(4, '0')}`);
  const inferredRange = inferPageRange({ question: q, answer: a });
  const pageRange =
    Array.isArray(r.page_range) && r.page_range.length >= 2
      ? [Number(r.page_range[0]), Number(r.page_range[1])]
      : inferredRange;

  return {
    ...r,
    id,
    page_range: pageRange,
    question: q,
    answer: a,
    needs_review: Boolean(r.needs_review),
  };
}

export function pageFilename(pageIndex) {
  return `page_${String(pageIndex).padStart(4, '0')}.jpg`;
}

export function pageUrl(pageIndex, apiBaseUrl, kind = 'original', jobId = null) {
  const fn = pageFilename(pageIndex);
  if (kind === 'uploads') {
    // Used for /output/uploads_pages/<job_id>/<filename>
    if (!jobId) return '';
    return `${apiBaseUrl}/output/uploads_pages/${jobId}/${fn}`;
  }
  if (kind === 'file') {
    // Used for /output/file-pages/<file_stem>/<filename>
    if (!jobId) return '';
    return `${apiBaseUrl}/output/file-pages/${encodeURIComponent(jobId)}/${fn}`;
  }
  // kind === 'original'
  return `${apiBaseUrl}/output/original_pages/${fn}`;
}

export function pageIndexFromFilename(name) {
  const m = String(name || '').match(/page_(\d+)\.jpg$/);
  return m ? Number(m[1]) : null;
}

export function bboxesFromComponents(obj) {
  const comps = [...(obj?.components || []), ...(obj?.image_components || [])];
  const out = [];
  for (const c of comps) {
    const b = c?.bbox;
    if (Array.isArray(b) && b.length >= 4) out.push(b.map(Number));
  }
  if (!out.length) {
    const ref = obj?.reference_bbox || [];
    for (const b of ref) {
      if (Array.isArray(b) && b.length >= 4) out.push(b.map(Number));
    }
    const single = obj?.bbox;
    if (!out.length && Array.isArray(single) && single.length >= 4) out.push(single.map(Number));
  }
  return out;
}

export function boxesByPageFromComponents(obj, fallbackPageIndex) {
  const groups = new Map();
  const comps = [...(obj?.components || []), ...(obj?.image_components || [])];
  const pageFallback =
    Number.isFinite(Number(fallbackPageIndex))
      ? Number(fallbackPageIndex)
      : Number.isFinite(Number(obj?.page_indices?.[0]))
        ? Number(obj.page_indices[0])
        : 0;

  // Ensure pages exist even if there are zero bboxes.
  const pages = new Set();
  (obj?.page_indices || []).forEach((p) => {
    if (Number.isFinite(Number(p))) pages.add(Number(p));
  });
  for (const c of comps) {
    if (Number.isFinite(Number(c?.page_index))) pages.add(Number(c.page_index));
  }
  if (pages.size === 0 && Array.isArray(obj?.page_indices)) {
    for (const p of obj.page_indices) {
      if (Number.isFinite(Number(p))) pages.add(Number(p));
    }
  }
  if (pages.size === 0) pages.add(pageFallback);
  for (const p of pages) groups.set(p, []);

  for (const c of comps) {
    const b = c?.bbox;
    if (!Array.isArray(b) || b.length < 4) continue;
    const pageIndex = Number.isFinite(Number(c?.page_index)) ? Number(c.page_index) : pageFallback;
    const arr = groups.get(pageIndex) || [];
    arr.push(b.map(Number));
    groups.set(pageIndex, arr);
  }

  // Fallback for older/newer record shapes with only reference_bbox/bbox.
  if ([...groups.values()].every((arr) => (arr || []).length === 0)) {
    const fallbackBoxes = [];
    for (const b of obj?.reference_bbox || []) {
      if (Array.isArray(b) && b.length >= 4) fallbackBoxes.push(b.map(Number));
    }
    const single = obj?.bbox;
    if (!fallbackBoxes.length && Array.isArray(single) && single.length >= 4) fallbackBoxes.push(single.map(Number));
    if (fallbackBoxes.length) groups.set(pageFallback, fallbackBoxes);
  }

  return [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([pageIndex, boxes]) => ({ pageIndex, boxes }));
}

export function drawBoxes({ imgEl, canvasEl, boxes, color }) {
  if (!imgEl || !canvasEl) return;
  const ctx = canvasEl.getContext('2d');
  if (!ctx) return;

  const w = imgEl.clientWidth || 0;
  const h = imgEl.clientHeight || 0;
  if (w <= 1 || h <= 1) return;

  canvasEl.width = Math.floor(w);
  canvasEl.height = Math.floor(h);
  ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);

  const nw = imgEl.naturalWidth || 1;
  const nh = imgEl.naturalHeight || 1;
  const sx = canvasEl.width / nw;
  const sy = canvasEl.height / nh;

  ctx.lineWidth = 2;
  ctx.strokeStyle = color || '#22c55e';
  ctx.fillStyle = 'rgba(34, 197, 94, 0.12)';

  (boxes || []).forEach((b) => {
    const [x1, y1, x2, y2] = b;
    const x = x1 * sx;
    const y = y1 * sy;
    const bw = (x2 - x1) * sx;
    const bh = (y2 - y1) * sy;
    ctx.fillRect(x, y, bw, bh);
    ctx.strokeRect(x, y, bw, bh);
  });
}

export function clearCanvas(canvasEl) {
  if (!canvasEl) return;
  const ctx = canvasEl.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
}

export function hitTestGroupIndex(img, canvas, groups, offsetX, offsetY) {
  const px = offsetX * ((img.naturalWidth || 1) / Math.max(1, canvas.clientWidth || 1));
  const py = offsetY * ((img.naturalHeight || 1) / Math.max(1, canvas.clientHeight || 1));
  for (let gi = groups.length - 1; gi >= 0; gi -= 1) {
    for (const b of groups[gi].boxes || []) {
      const [x1, y1, x2, y2] = b;
      if (px >= x1 && px <= x2 && py >= y1 && py <= y2) return gi;
    }
  }
  return -1;
}

export function recordPageSet(rec, field) {
  const out = new Set();
  for (const p of rec?.[field]?.page_indices || []) {
    const n = Number(p);
    if (Number.isFinite(n)) out.add(n);
  }
  return out;
}

export function candidateAnswerGroupsForPage({ records, pageIndex, targetId }) {
  const target = (records || []).find((r) => r.id === targetId) || null;
  const tQuestionPages = recordPageSet(target, 'question');
  const tRange = Array.isArray(target?.page_range) ? target.page_range.map(Number) : null;

  const tier1 = [];
  const tier2 = [];
  const tier3 = [];

  for (const r of records || []) {
    const groups = boxesByPageFromComponents(r?.answer, pageIndex);
    const g = groups.find((x) => Number(x.pageIndex) === Number(pageIndex));
    if (!g) continue;
    const boxes = (g.boxes || []).filter((b) => Array.isArray(b) && b.length >= 4);
    if (!boxes.length) continue;
    const row = { boxes, sourceId: r.id };
    const rAnswerPages = recordPageSet(r, 'answer');
    if ([...rAnswerPages].some((p) => tQuestionPages.has(p))) {
      tier1.push(row);
      continue;
    }
    if (tRange && tRange.length >= 2) {
      const rRange = Array.isArray(r?.page_range) ? r.page_range.map(Number) : null;
      if (rRange && rRange.length >= 2) {
        const d = Math.min(Math.abs(rRange[0] - tRange[0]), Math.abs(rRange[1] - tRange[1]));
        if (d <= 1) {
          tier2.push(row);
          continue;
        }
      }
    }
    tier3.push(row);
  }

  if (tier1.length) return tier1;
  if (tier2.length) return tier2;
  return tier3;
}

export function mapperPageIndices({ originalFiles }) {
  return (originalFiles || [])
    .map((f) => pageIndexFromFilename(f))
    .filter((x) => Number.isFinite(x))
    .sort((a, b) => a - b);
}

export function neighborPage(pages, current, delta) {
  const idx = pages.indexOf(current);
  if (idx < 0) return current;
  const ni = idx + delta;
  if (ni < 0 || ni >= pages.length) return current;
  return pages[ni];
}

